import { Box } from '@/shared/ui';

import type { KeyboardEvent as ReactKeyboardEvent } from 'react';

import { FactsheetDialogButton } from '@/features/factsheet';
import type { SpotHistoryTableColumnType } from '@/features/markets/types';
import { stopPropagation } from '@/shared/lib/dom/stopPropagation';

import { getColumnCode } from './spotHistoryColumn';

type DetailColumn = {
  key: 'alias' | 'code' | 'currency' | 'units' | 'info';
  label: string;
  align?: 'left' | 'right';
  width?: string;
};

const DETAIL_COLUMNS: readonly DetailColumn[] = [
  { key: 'alias', label: 'Alias', width: '25%' },
  { key: 'code', label: 'Code' },
  { key: 'currency', label: 'Currency', align: 'right', width: '18%' },
  { key: 'units', label: 'Units', width: '21%' },
  { key: 'info', label: 'Info', align: 'right', width: '8%' }
];

const stopKeyboardPropagation = (event: ReactKeyboardEvent) => {
  event.stopPropagation();
};

const HeaderExpandedDetails = ({ column }: { column: SpotHistoryTableColumnType }) => {
  const code = getColumnCode(column);
  const values: Record<DetailColumn['key'], string | undefined> = {
    alias: column.alias ?? undefined,
    code,
    currency: column.currency ?? undefined,
    units: column.units ?? undefined,
    info: undefined
  };

  return (
    <Box
      component='table'
      sx={{
        borderCollapse: 'collapse',
        maxWidth: 280,
        mt: 1,
        tableLayout: 'auto',
        width: 'auto',
        mx: 1
      }}
    >
      <Box component='colgroup'>
        {DETAIL_COLUMNS.map(detailColumn => (
          <Box
            key={detailColumn.key}
            component='col'
            sx={{ width: detailColumn.width ?? 'auto' }}
          />
        ))}
      </Box>
      <Box component='thead'>
        <Box component='tr'>
          {DETAIL_COLUMNS.map(detailColumn => (
            <Box
              key={detailColumn.key}
              component='th'
              scope='col'
              sx={{
                fontSize: 10,
                fontWeight: 400,
                lineHeight: 1.2,
                overflow: 'hidden',
                pl: 2,
                whiteSpace: 'nowrap',
                '&:first-of-type': { pl: 0 },
                textAlign: detailColumn.align ?? 'left',
                textOverflow: 'ellipsis'
              }}
            >
              {detailColumn.label}
            </Box>
          ))}
        </Box>
      </Box>
      <Box component='tbody'>
        <Box component='tr'>
          {DETAIL_COLUMNS.map(detailColumn => (
            <Box
              key={`${code}-${detailColumn.key}`}
              component='td'
              sx={{
                color: 'gx.text',
                fontSize: 12,
                lineHeight: 1.3,
                overflow: 'hidden',
                pl: 2,
                whiteSpace: 'nowrap',
                '&:first-of-type': { pl: 0 },
                textAlign: detailColumn.align ?? 'left',
                textOverflow: 'ellipsis'
              }}
            >
              {detailColumn.key === 'info' ? (
                <Box
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    height: 18,
                    justifyContent: 'flex-end'
                  }}
                  onClick={stopPropagation}
                  onKeyDown={stopKeyboardPropagation}
                >
                  <FactsheetDialogButton
                    code={code}
                    fontSize={14}
                    showTooltip
                  />
                </Box>
              ) : (
                values[detailColumn.key] || '-'
              )}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default HeaderExpandedDetails;
